import React from "react";
import { createColumnHelper } from "@tanstack/react-table";
import { TagColor } from "app/data/common/tagColor";
import TerminalDetails from "ui-components/terminal-details/terminalDetails";
import Tag from "ui-components/molecules/tag/tag";
import _ from "lodash";
import XGSNavLink from "ui-components/xgs-nav-link/xgsNavLink";
import { XGSSelectOption } from "ui-components/xgs-select/xgsSelect";
import moment from "moment";
import { ReactComponent as ArriveLoadIcon } from "../../../images/loaded_truck.svg";
import { ReactComponent as DoorIcon } from "../../../images/door.svg";
// import { ReactComponent as EditIcon } from "../../../images/edit_pencil.svg";
import { ReactComponent as CloseDoorIcon } from "../../../images/door_lock.svg";
import { ReactComponent as CloseDoorGreyIcon } from "../../../images/door_lock_grey.svg";

const columnHelper = createColumnHelper<any>();

const getStatusColor = (status: string) => {
  if (status === 'ARRIVED') {
    return TagColor.GREEN;
  }
  if (status === 'UNLOADING') {
    return TagColor.PURPLE;
  }
  if (status === 'LATE' || status === 'ALERT') {
    return TagColor.BLUE;
  }
  if (status === 'INBOUND' || status === 'IN_TRANSIT') {
    return TagColor.BLUE;
  }
  if (status === 'UNLOADED' || status === 'STAGED') {
    return TagColor.GREY;
  }
  return TagColor.GREY;
}

const getArrivalStatus = (status: string, arrivalDate?: string, arrivalTime?: string) => {
  if (status === 'ARRIVED') {
    return "Arrived at";
  }
  if (status === 'LATE') {
    return "Expected Arrival Late";
  }
  if (status === 'ALERT') {
    return "Expected Arrival Alerted";
  }
  if (status === 'INBOUND') {
    return "Expected Arrival Inbound";
  }
  return "";
}

const formatStatusLabel = (value?: string) => {
  if (!value) return "-";
  return value
    .toString()
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

export const getInboundFreightsColumn = (
  onAssignDoorClick?: (rowData: any) => void,
  onArriveLoadClick?: (rowData: any) => void,
  onCloseDoorClick?: (rowData: any) => void,
  destinationTerminalName?: string
) => [
  columnHelper.accessor("status", {
    id: "status",
    header: "Status",
    cell: (props) => {
      const rowData = props.row.original;
      const status = rowData?.freightStatus || rowData?.status || '';
      return (
        <div className="inbound-freight-status-badge" data-status={status}>
          <Tag mods={{ color: getStatusColor(status) }}>
            {formatStatusLabel(status)}
          </Tag>
        </div>
      );
    },
    size: 90,
    minSize: 90,
  }),

  columnHelper.accessor(
    (row) => ({
      arrivalDate: row.arrivalDate,
      arrivalTime: row.arrivalTime,
    }),
    {
      id: "arrivalEta",
      header: "Arrival / ETA",
      cell: ({ getValue, row }) => {
        const { arrivalDate, arrivalTime } = getValue();
        const status = row?.original?.status || '';
        const arrivalStatus = getArrivalStatus(status, arrivalDate, arrivalTime);
        const isLate = status === 'LATE';
        const isAlerted = status === 'ALERT';
        const isInbound = status === 'INBOUND' || status === 'IN_TRANSIT';
        
        let statusColor = '#777';
        if (isLate) {
          statusColor = '#F00'; // Red for late
        } else if (isAlerted) {
          statusColor = '#E16C00'; // Orange for alerted
        } else if (isInbound) {
          statusColor = '#2F80ED'; // Blue for inbound/in-transit
        }
        const expectedArrivalPrefix = "Expected Arrival";
        const hasExpectedArrivalPrefix =
          arrivalStatus === expectedArrivalPrefix ||
          arrivalStatus.startsWith(`${expectedArrivalPrefix} `);
        const expectedArrivalSuffix = hasExpectedArrivalPrefix
          ? (arrivalStatus === expectedArrivalPrefix
              ? ""
              : arrivalStatus.replace(expectedArrivalPrefix, "").trim())
          : "";

        const arrivalDateTime =
          row?.original?.actualArrivalDateTime ||
          row?.original?.estimatedArrivalDateTime ||
          (arrivalDate && arrivalTime ? `${arrivalDate} ${arrivalTime}` : null);

        const formattedDate = arrivalDateTime
          ? moment(arrivalDateTime).format("MM/DD/YY")
          : '-';
        const formattedTime = arrivalDateTime
          ? moment(arrivalDateTime).format("hh:mm A")
          : '-';

        return (
          <div>
            <div>{formattedDate} &nbsp; {formattedTime}</div>
            {arrivalStatus && (
              <div
                className="inbound-freight-arrival-eta-status"
                style={{
                  fontSize: '12px',
                  fontStyle: 'normal',
                  fontWeight: 400,
                  color: hasExpectedArrivalPrefix ? '#777' : statusColor,
                }}
              >
                {hasExpectedArrivalPrefix ? (
                  <>
                    {expectedArrivalPrefix}
                    {expectedArrivalSuffix && (
                      <>
                        {" "}
                        <span style={{ color: statusColor, marginLeft: "4px" }}>
                          {expectedArrivalSuffix}
                        </span>
                      </>
                    )}
                  </>
                ) : (
                  arrivalStatus
                )}
              </div>
            )}
          </div>
        );
      },
      size: 155,
      minSize: 155,
    }
  ),

  columnHelper.accessor((row) => row.assignedDoorCode ?? row.doorNumber, {
    id: "doorNumber",
    header: "Door",
    cell: (props) => {
      const rowData = props.row.original;
      const status = rowData?.freightStatus || rowData?.status;
      const assignedDoorCode = rowData?.assignedDoorCode || rowData?.doorNumber;
      const doorAction = rowData?.doorAction;
      const doorActionLabel = rowData?.doorActionLabel;
      const doorLabel = assignedDoorCode
        ? (() => {
            const normalized = assignedDoorCode.toString().toUpperCase();
            return normalized.startsWith("D-") ? normalized : `D-${normalized}`;
          })()
        : "";
      const selectedDoor: XGSSelectOption | null = assignedDoorCode
        ? { label: doorLabel, value: assignedDoorCode }
        : null;

      if (selectedDoor) {
        const showUnloading = status === 'UNLOADING' || status === 'UNLOADED';
        const showAtDoor = status === 'ARRIVED' && assignedDoorCode;
        const piecesUnloaded = rowData?.piecesUnloaded;
        const piecesCountRaw = rowData?.piecesCount;
        const piecesCount = piecesCountRaw != null && piecesCountRaw !== ''
          ? Number(piecesCountRaw)
          : 0;
        const showUnloadingProgress =
          showUnloading &&
          typeof piecesUnloaded === 'number' &&
          piecesCount > 0;
        const unloadingPercent = showUnloadingProgress
          ? Math.min(100, Math.round((piecesUnloaded / piecesCount) * 100))
          : 0;

        return (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span>{selectedDoor.label}</span>
              {(status === 'UNLOADING' || status === 'UNLOADED') && (
                <CloseDoorGreyIcon style={{ width: '14px', height: '14px' }} />
              )}
              {/* TODO: Restore edit door icon when feature is ready */}
            </div>
            {showUnloading ? (
              <div className="inbound-freight-door-unloading">
                {showUnloadingProgress ? (
                  <>
                    <div className="inbound-freight-unloading-progress__header">
                      <span style={{ fontSize: '12px', color: '#777' }}>Unloading</span>
                      <span className="inbound-freight-unloading-progress__percent">
                        {unloadingPercent}%
                      </span>
                    </div>
                    <div className="inbound-freight-unloading-progress__track">
                      <div
                        className="inbound-freight-unloading-progress__bar"
                        style={{ width: `${unloadingPercent}%` }}
                      />
                    </div>
                  </>
                ) : (
                  <span style={{ fontSize: '12px', color: '#777' }}>Unloading</span>
                )}
              </div>
            ) : showAtDoor ? (
              <div style={{ fontSize: '12px', color: '#777' }}>
                At Door
              </div>
            ) : null}
          </div>
        );
      }

      // No door assigned: show doorActionLabel from API (e.g. "Assign Door")
      const showAssignDoor = doorAction === 'ASSIGN_DOOR' || (!assignedDoorCode && (status === 'ARRIVED' || status === 'UNLOADING' || status === 'UNLOADED' || status === 'IN_TRANSIT' || status === 'INBOUND'));
      const linkLabel = doorActionLabel || "Assign Door";

      if (showAssignDoor && (status === 'ARRIVED' || status === 'UNLOADING' || status === 'UNLOADED')) {
        return (
          <div className="inbound-freight-assign-door-cell">
            <button
              type="button"
              className="inbound-freight-assign-door-link"
              onClick={() => onAssignDoorClick?.(rowData)}
            >
              {linkLabel}
            </button>
            {status === 'ARRIVED' && (
              <div style={{ fontSize: '12px', color: '#777' }}>
                At Yard
              </div>
            )}
          </div>
        );
      }

      if (showAssignDoor && (status === 'IN_TRANSIT' || status === 'INBOUND')) {
        return (
          <div className="inbound-freight-assign-door-cell" style={{ fontSize: '14px', color: '#8A8F98', whiteSpace: 'nowrap' }}>
            {linkLabel}
          </div>
        );
      }

      return '-';
    },
    size: 120,
    minSize: 120,
  }),

  columnHelper.display({
    id: "nextStep",
    header: "Next Step",
    cell: ({ row }) => {
      const rowData = row.original;
      const status = rowData?.freightStatus || rowData?.status || '';
      const doorNumber = rowData?.assignedDoorCode || rowData?.doorNumber;
      const nextStepActionLabel = rowData?.nextStepActionLabel;
      const nextStepAction = rowData?.nextStepAction;
      const nextStepEnabled = rowData?.nextStepEnabled !== false;
      const actions: JSX.Element[] = [];

      // When nextStepEnabled is false but we have UNLOADING status with progress, show informational display
      if (!nextStepEnabled && (nextStepAction === 'UNLOADING' || status === 'UNLOADING' || status === 'UNLOADED')) {
        const piecesUnloaded = rowData?.piecesUnloaded;
        const piecesCountRaw = rowData?.piecesCount;
        const piecesCount = piecesCountRaw != null && piecesCountRaw !== ''
          ? Number(piecesCountRaw)
          : 0;
        const showUnloadingProgress =
          typeof piecesUnloaded === 'number' &&
          piecesCount > 0;
        const unloadingPercent = showUnloadingProgress
          ? Math.min(100, Math.round((piecesUnloaded / piecesCount) * 100))
          : 0;

        return (
          <div className="inbound-freight-next-step-cell">
            {showUnloadingProgress ? (
              <div className="inbound-freight-door-unloading">
                <div className="inbound-freight-unloading-progress__header">
                  <span style={{ fontSize: '14px', color: '#555' }}>
                    {nextStepActionLabel || "Unloading"}
                  </span>
                  <span className="inbound-freight-unloading-progress__percent">
                    {unloadingPercent}%
                  </span>
                </div>
                <div className="inbound-freight-unloading-progress__track">
                  <div
                    className="inbound-freight-unloading-progress__bar"
                    style={{ width: `${unloadingPercent}%` }}
                  />
                </div>
              </div>
            ) : (
              <span style={{ fontSize: '14px', color: '#555' }}>
                {nextStepActionLabel || "Unloading"}
              </span>
            )}
          </div>
        );
      }

      if (nextStepEnabled && (nextStepAction === "ARRIVE" || (!nextStepAction && (status === 'IN_TRANSIT' || status === 'INBOUND')))) {
        actions.push(
          <button
            key="arrive-load"
            className="inbound-freight-next-step-action"
            onClick={() => onArriveLoadClick?.(rowData)}
            style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '4px',
              background: 'none',
              border: 'none',
              color: '#2F80ED',
              cursor: 'pointer',
              fontSize: '14px',
              padding: 0
            }}
          >
            <ArriveLoadIcon style={{ width: '16px', height: '16px' }} />
            {nextStepActionLabel || "Arrive Load"}
          </button>
        );
      }

      if (nextStepEnabled && (nextStepAction === "ASSIGN_DOOR" || (!nextStepAction && status === 'ARRIVED')) && !doorNumber) {
        actions.push(
          <button
            key="assign-door"
            className="inbound-freight-next-step-action"
            onClick={() => onAssignDoorClick?.(rowData)}
            style={{ 
              display: 'inline-flex', 
              alignItems: 'center', 
              gap: '4px',
              background: 'none',
              border: 'none',
              color: '#2F80ED',
              cursor: 'pointer',
              fontSize: '14px',
              padding: 0,
              whiteSpace: 'nowrap',
              flexShrink: 0,
              minWidth: 'min-content'
            }}
          >
            <DoorIcon style={{ width: '16px', height: '16px', flexShrink: 0 }} />
            <span style={{ whiteSpace: 'nowrap', flexShrink: 0 }}>{nextStepActionLabel || "Assign Door"}</span>
          </button>
        );
      }

      if (nextStepEnabled && (nextStepAction === "CLOSE_DOOR" || (!nextStepAction && (status === 'UNLOADING' || status === 'UNLOADED')))) {
        actions.push(
          <button
            key="close-door"
            className="inbound-freight-next-step-action"
            onClick={() => onCloseDoorClick?.(rowData)}
            style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '4px',
              background: 'none',
              border: 'none',
              color: '#2F80ED',
              cursor: 'pointer',
              fontSize: '14px',
              padding: 0
            }}
          >
            <CloseDoorIcon style={{ width: '16px', height: '16px' }} />
            {nextStepActionLabel || "Close Door"}
          </button>
        );
      }

      if (actions.length === 0) {
        return '-';
      }

      return (
        <div className="inbound-freight-next-step-cell" style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
          {actions}
        </div>
      );
    },
    size: 136,
    minSize: 116,
  }),

  columnHelper.accessor("trailerNumber", {
    id: "trailerNumber",
    header: "Trailer",
    cell: (props) => {
      const trailer = props.getValue();
      return (
        <div style={{ whiteSpace: 'nowrap' }}>
          {!_.isEmpty(trailer) ? `TRL-${trailer?.toString()}` : '-'}
        </div>
      );
    },
    size: 110,
    minSize: 100,
  }),

  columnHelper.accessor("type", {
    id: "type",
    header: "Type",
    cell: (props) => {
      return (
        <Tag mods={{ color: TagColor.GREY }}>
          Linehaul
        </Tag>
      );
    },
    size: 80,
    minSize: 80,
  }),

  columnHelper.accessor("manifestNumber", {
    id: "manifestNumber",
    header: "Manifest",
    cell: (props) => {
      const manifestNumber = props.getValue();
      if (!manifestNumber) return '-';
      return (
        <XGSNavLink
          className="xgs-table-link-cell"
          to={`/shipments?lm=${manifestNumber}`}
          target="_blank"
        >
          MF-{manifestNumber}
        </XGSNavLink>
      );
    },
    size: 95,
    minSize: 95,
  }),

  columnHelper.accessor(
    (row) =>
      row.originCity ||
      row.originTerminalName ||
      row.originAirportCode ||
      row.originTerminal ||
      row.origin,
    {
    id: "originCity",
    header: "Origin Terminal",
    cell: (cellProps) => {
      const value = cellProps.getValue();
      if (!value) return '-';
      return (
        <div style={{ whiteSpace: 'nowrap' }}>
          <TerminalDetails terminalName={value.toString().toUpperCase()} />
        </div>
      );
    },
    size: 187,
    minSize: 177,
  }),

  columnHelper.accessor(
    "originDate",
    {
      id: "originDate",
      header: "Dispatched Date",
      cell: (props) => {
        const value = props.getValue();
        const formatted = value ? moment(value).format("MM/DD/YY") : "-";
        return <>
          <div style={{ whiteSpace: 'nowrap' }}>
            {formatted}
          </div>
        </>
      },
      size: 90,
      minSize: 90,
    }
  ),

  columnHelper.accessor("probillCount", {
    id: "probillCount",
    header: "Probills",
    cell: (props) => !_.isEmpty(props.getValue()) ? props.getValue() : '-',
    size: 70,
    minSize: 70,
  }),

  columnHelper.accessor("piecesCount", {
    id: "piecesCount",
    header: "Pieces",
    cell: (props) => !_.isEmpty(props.getValue()) ? props.getValue() : '-',
    size: 60,
    minSize: 60,
  }),

  columnHelper.accessor("lbs", {
    id: "lbs",
    header: "Weight (lbs)",
    cell: (props) => props.getValue() ? Number(props.getValue())?.toLocaleString("en-US") : '-',
    size: 80,
    minSize: 80,
  }),
];
